import { Suspense, lazy } from "react"
import { Navigate, useLocation, type RouteObject } from "react-router"

import { LoadingFallback } from "./views/_loading"
import { RouteError } from "./views/_error"
import { LibraryLayout } from "./views/library/_layout"
import ProductDashboard from "./views/_product-dashboard"
import { ProductRootGate } from "@/contexts/product-root-gate"
import { DefaultProductRedirect } from "@/contexts/default-product-redirect"
import { TENANT_PRODUCT_SHELL_HUB_SEGMENTS } from "@exxatdesignux/product-framework"
import { PRISM_HUB_SEGMENTS, ONE_SITES_HUB_SEGMENTS } from "@/lib/mock/navigation"
import { getStorageItem } from "@exxatdesignux/ui/lib/persisted-state"

/** Per-device flag set when the builder onboarding flow completes. */
const ONBOARDING_COMPLETE_KEY = "builder:onboarding-complete:v1"

/**
 * Vite route table — implements the multi-product routing pattern
 * (`apps/web/docs/multi-product-routing-pattern.md`):
 *
 *  - **Per-product dashboards** live under `/<product-root>/dashboard`
 *    (Rule 1). Switching products navigates to that root's dashboard
 *    (Rule 2 — driven by `useProductSwitch` in
 *    `contexts/product-route-sync.tsx`).
 *  - **Back-compat redirects** at the workspace root (`/dashboard` →
 *    `/prism/dashboard`) keep deep links from before this refactor working
 *    without a server-side 308.
 *  - **Library** mounts under product roots (`/<product-root>/library/*`).
 *    The legacy root `/library/*` mount stays as a back-compat alias while
 *    the Library subsystem normalizes product prefixes internally.
 *  - **Shell-global settings** — profile prefs at `/settings/profile`;
 *    org/workspace (products) at `/settings/organization` and
 *    `/<product-root>/settings`. `/settings` redirects for back-compat.
 *  - **`/help`** stays shell-global (per *user*, not per *product*).
 *  - **DS demo routes** (`/columns`, `/tokens-themes`) also stay at the
 *    root while they're still development surfaces — same exception.
 */

/** Back-compat: `/library`, `/columns`, `/tokens-themes`, `/exam` → Design OS product root. */
function RedirectToDesignOsSubpath({ fromPrefix, toPrefix }: { fromPrefix: string; toPrefix: string }) {
  const location = useLocation()
  const rest = location.pathname.startsWith(fromPrefix)
    ? location.pathname.slice(fromPrefix.length)
    : ""
  const normalizedRest = rest.startsWith("/") ? rest : rest ? `/${rest}` : ""
  return (
    <Navigate
      to={`${toPrefix}${normalizedRest}${location.search}${location.hash}`}
      replace
    />
  )
}

function lazyPage(
  loader: () => Promise<{ default: React.ComponentType }>,
  fallback: React.ReactNode = <LoadingFallback />,
) {
  const Lazy = lazy(loader)
  return (
    <Suspense fallback={fallback}>
      <Lazy />
    </Suspense>
  )
}

/**
 * Back-compat redirect for `/dashboard` → default product dashboard.
 */
function LegacyRedirectToDefaultDashboard() {
  return <DefaultProductRedirect />
}

// First-visit landing — run the builder onboarding flow on the very first
// visit, then land on the products home on every subsequent load.
// Onboarding marks itself complete via `builder:onboarding-complete:v1`
// (set in `views/builder-onboarding.tsx`), so the redirect only fires once.
// Re-run it from `Settings → Profile → Restart onboarding` (which clears the
// flag) or by visiting `/builder/onboarding` directly.
//
// `/` used to drop straight into the default product's dashboard. It now stops
// at `/home` so someone with more than one product picks rather than getting
// dealt one — `/dashboard` still shortcuts to the default for anyone who
// bookmarked it.
function FirstRunRedirect() {
  const onboardingComplete = getStorageItem(ONBOARDING_COMPLETE_KEY) === "true"
  if (!onboardingComplete) {
    return <Navigate to="/builder/onboarding" replace />
  }
  return <Navigate to="/home" replace />
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-product children
// ─────────────────────────────────────────────────────────────────────────────
//
// Today only the dashboard is under a product root. Once Library / Placements
// / Settings sub-screens move under products, append their children here (or
// extract per-product factories like `prismChildren()`, `oneSitesChildren()`).

const TENANT_PRODUCT_SHELL_HUBS = TENANT_PRODUCT_SHELL_HUB_SEGMENTS.filter(
  segment => segment !== "library",
)

/** Prism + One — Schools + One — Sites + legacy tenant shell hubs. */
/**
 * Nav segments that have a real page and must not fall through to
 * `_product-shell-placeholder`. Keep in sync with the explicit routes in
 * `productChildren()`.
 */
const BUILT_HUB_SEGMENTS = new Set([
  "learning-activities",
  "locations",
  "slot-requests",
])

const PRODUCT_SHELL_HUB_SEGMENTS = [
  ...new Set([
    ...PRISM_HUB_SEGMENTS,
    ...ONE_SITES_HUB_SEGMENTS,
    ...TENANT_PRODUCT_SHELL_HUBS,
  ]),
]

function libraryChildren(): RouteObject[] {
  return [
    { index: true, element: lazyPage(() => import("./views/library/index")) },
    { path: "all", element: lazyPage(() => import("./views/library/all")) },
    { path: "find", element: lazyPage(() => import("./views/library/find")) },
    { path: "list", element: lazyPage(() => import("./views/library/list")) },
    { path: "new", element: lazyPage(() => import("./views/library/new")) },
  ]
}

function learningActivitiesChildren(): RouteObject[] {
  return [
    { index: true, element: lazyPage(() => import("./views/learning-activities")) },
    {
      path: "courses/:offeringId",
      element: lazyPage(() => import("./views/learning-activities-course-detail")),
    },
  ]
}

function productChildren(): RouteObject[] {
  return [
    { index: true, element: <Navigate to="dashboard" replace /> },
    {
      path: "dashboard",
      element: <ProductDashboard />,
      errorElement: <RouteError />,
    },
    {
      path: "library",
      element: <LibraryLayout />,
      children: libraryChildren(),
      errorElement: <RouteError />,
    },
    {
      path: "leo",
      element: lazyPage(() => import("./views/leo")),
      errorElement: <RouteError />,
    },
    {
      path: "design-system",
      element: lazyPage(() => import("./views/design-system/_layout")),
      errorElement: <RouteError />,
      children: [
        { index: true, element: lazyPage(() => import("./views/design-system/index")) },
        { path: ":slug", element: lazyPage(() => import("./views/design-system/doc")) },
      ],
    },
    {
      path: "columns",
      element: lazyPage(() => import("./views/columns")),
      errorElement: <RouteError />,
    },
    {
      path: "column-types-demo",
      element: lazyPage(() => import("./views/column-types-demo")),
      errorElement: <RouteError />,
    },
    {
      path: "tokens-themes",
      element: lazyPage(() => import("./views/tokens-themes")),
      errorElement: <RouteError />,
    },
    {
      path: "focus-workflow",
      element: lazyPage(() => import("./views/focus-workflow")),
      errorElement: <RouteError />,
    },
    {
      path: "exam-lock",
      element: lazyPage(() => import("./views/exam-lock")),
      errorElement: <RouteError />,
    },
    {
      path: "learning-activities",
      element: lazyPage(() => import("./views/learning-activities/_layout")),
      children: learningActivitiesChildren(),
      errorElement: <RouteError />,
    },
    // Exxat One hubs. These four are real surfaces now, so they must be
    // declared before the placeholder catch-all below and excluded from it.
    {
      path: "locations",
      element: lazyPage(() => import("./views/one/locations")),
      errorElement: <RouteError />,
    },
    {
      path: "slot-requests",
      element: lazyPage(() => import("./views/one/slot-requests")),
      errorElement: <RouteError />,
    },
    ...PRODUCT_SHELL_HUB_SEGMENTS.filter(
      segment => !BUILT_HUB_SEGMENTS.has(segment),
    ).map(segment => ({
      path: `${segment}/*`,
      element: lazyPage(() => import("./views/_product-shell-placeholder")),
      errorElement: <RouteError />,
    })),
    {
      path: "settings",
      element: lazyPage(() => import("./views/settings-organization")),
      errorElement: <RouteError />,
    },
  ]
}

export const routes: RouteObject[] = [
  // ───────────────────────────────────────────────────────────────────────
  // Root + back-compat redirects
  // ───────────────────────────────────────────────────────────────────────
  // Cold-start landing → onboarding on the first visit, then the products home.
  { index: true, element: <FirstRunRedirect /> },

  // Pre-product back-compat for the only previously-rooted hub.
  { path: "dashboard", element: <LegacyRedirectToDefaultDashboard /> },

  // ───────────────────────────────────────────────────────────────────────
  // Products home — shell-global. The marketing pages live here rather than
  // under a product root because their audience is people without that
  // product; routing them through `/prism/*` would adopt a product context
  // they are not entitled to.
  // ───────────────────────────────────────────────────────────────────────
  {
    path: "home",
    errorElement: <RouteError />,
    children: [
      { index: true, element: lazyPage(() => import("./views/product-home")) },
      {
        path: ":productSlug",
        element: lazyPage(() => import("./views/product-marketing")),
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // Shell-global routes (per-user, not per-product)
  // ───────────────────────────────────────────────────────────────────────
  {
    path: "library/*",
    element: <RedirectToDesignOsSubpath fromPrefix="/library" toPrefix="/design-os/library" />,
  },
  {
    path: "settings",
    element: lazyPage(() => import("./views/settings-layout")),
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: lazyPage(() => import("./views/settings-legacy-redirect")),
      },
      {
        path: "profile",
        element: lazyPage(() => import("./views/settings-profile")),
      },
      {
        path: "organization",
        element: lazyPage(() => import("./views/settings-organization")),
      },
    ],
  },
  {
    path: "builder/onboarding",
    element: lazyPage(() => import("./views/builder-onboarding")),
    errorElement: <RouteError />,
  },
  {
    path: "builder/products",
    element: <Navigate to="/settings/organization" replace />,
  },
  {
    path: "help",
    element: lazyPage(() => import("./views/help")),
    errorElement: <RouteError />,
  },

  // ───────────────────────────────────────────────────────────────────────
  // DS demo routes (development surfaces — still at root per the rule's
  // exception; once promoted into a product hub they adopt that subpath).
  // ───────────────────────────────────────────────────────────────────────
  {
    path: "design-system/*",
    element: (
      <RedirectToDesignOsSubpath fromPrefix="/design-system" toPrefix="/design-os/design-system" />
    ),
  },
  {
    path: "columns/*",
    element: <RedirectToDesignOsSubpath fromPrefix="/columns" toPrefix="/design-os/columns" />,
  },
  {
    path: "tokens-themes/*",
    element: (
      <RedirectToDesignOsSubpath fromPrefix="/tokens-themes" toPrefix="/design-os/tokens-themes" />
    ),
  },
  {
    path: "exam/*",
    element: <RedirectToDesignOsSubpath fromPrefix="/exam" toPrefix="/design-os/exam-lock" />,
  },

  // ───────────────────────────────────────────────────────────────────────
  // Directory products — People, Courses, Personnel (separate roots), plus
  // Administrator overview. Legacy `/admin/{hub}` paths redirect to the new
  // product roots so bookmarks keep working.
  // ───────────────────────────────────────────────────────────────────────
  {
    path: "people",
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/people/students" replace /> },
      { path: "students", element: lazyPage(() => import("./views/admin/people-students")) },
      { path: "faculty", element: lazyPage(() => import("./views/admin/people-faculty")) },
      { path: "staff", element: lazyPage(() => import("./views/admin/people-staff")) },
      {
        path: ":personId",
        element: lazyPage(() => import("./views/admin/person-detail")),
      },
      { path: "dashboard", element: <Navigate to="/people/students" replace /> },
    ],
  },
  {
    path: "courses",
    errorElement: <RouteError />,
    children: [
      { index: true, element: lazyPage(() => import("./views/admin/courses")) },
      {
        path: ":courseId",
        element: lazyPage(() => import("./views/admin/course-detail")),
      },
      { path: "dashboard", element: <Navigate to="/courses" replace /> },
    ],
  },
  {
    path: "personnel",
    errorElement: <RouteError />,
    children: [
      { index: true, element: lazyPage(() => import("./views/admin/personnel")) },
      {
        path: ":personnelId",
        element: lazyPage(() => import("./views/admin/personnel-detail")),
      },
      { path: "dashboard", element: <Navigate to="/personnel" replace /> },
    ],
  },
  {
    path: "admin",
    errorElement: <RouteError />,
    children: [
      { index: true, element: lazyPage(() => import("./views/admin/index")) },
      { path: "people", element: <Navigate to="/people" replace /> },
      {
        path: "people/*",
        element: (
          <RedirectToDesignOsSubpath fromPrefix="/admin/people" toPrefix="/people" />
        ),
      },
      { path: "courses", element: <Navigate to="/courses" replace /> },
      {
        path: "courses/*",
        element: (
          <RedirectToDesignOsSubpath fromPrefix="/admin/courses" toPrefix="/courses" />
        ),
      },
      { path: "personnel", element: <Navigate to="/personnel" replace /> },
      {
        path: "personnel/*",
        element: (
          <RedirectToDesignOsSubpath fromPrefix="/admin/personnel" toPrefix="/personnel" />
        ),
      },
      { path: "dashboard", element: <Navigate to="/admin" replace /> },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // Product-owned roots (Rule 1) — built-in slugs + custom suffix slugs.
  // MUST stay after shell-global routes so `/settings` etc. are not captured.
  // ───────────────────────────────────────────────────────────────────────
  {
    path: ":productRootSegment",
    element: <ProductRootGate />,
    children: productChildren(),
    errorElement: <RouteError />,
  },

  {
    path: "*",
    element: lazyPage(() => import("./views/_not-found")),
  },
]
